import {
  BarsOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useThemedLayoutContext } from '@refinedev/antd';
import { useGetIdentity, useLogout, useWarnAboutChange } from '@refinedev/core';
import { Avatar, Button, Dropdown, Layout, Space, Tooltip, Typography, theme } from 'antd';
import { useNavigate } from 'react-router';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { RentalContextIndicator } from '../RentalContextIndicator';
import { RentalSwitcher } from '../RentalSwitcher';
import { getImpersonatedRental } from '@/services/auth';
import { getUserRoleLabel, USER_ROLE, type UserRole } from '@/types/models';
import type { MenuProps } from 'antd';
import { useThemeMode } from '@/context/themeMode';
import { useIsTouchTier } from '@/hooks/useIsTouchTier';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

type UserIdentity = {
  id: string | number;
  name: string;
  email: string;
  role: UserRole;
  rental?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  avatar?: string;
};

export const Header = (): React.JSX.Element => {
  const { data: user } = useGetIdentity<UserIdentity>();
  const { mutate: logout } = useLogout();
  const { warnWhen: shouldWarnWhen, setWarnWhen } = useWarnAboutChange();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { resolvedMode, toggleMode } = useThemeMode();
  const isTouchTier = useIsTouchTier();
  const { mobileSiderOpen: isMobileSiderOpen, setMobileSiderOpen } = useThemedLayoutContext();
  const isImpersonating = user?.role === USER_ROLE.ADMIN && getImpersonatedRental() !== null;

  const menuItems: MenuProps['items'] = [
    ...(user
      ? [
          {
            key: 'identity',
            disabled: true,
            label: isImpersonating
              ? getUserRoleLabel(user.role)
              : `${user.name} · ${getUserRoleLabel(user.role)}`,
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => {
        void navigate('/profile');
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        // Logging out drops a half-filled form, so it asks first, like a route change.
        if (shouldWarnWhen) {
          if (!window.confirm('Are you sure you want to leave? You have unsaved changes.')) return;
          setWarnWhen(false);
        }
        logout();
      },
    },
  ];

  return (
    <>
      <AntHeader className="app-header" style={{ borderBottom: `1px solid ${token.colorBorder}` }}>
        <div className="app-header__start">
          {isTouchTier && (
            <Button
              type="text"
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={isMobileSiderOpen}
              icon={<BarsOutlined />}
              onClick={() => {
                setMobileSiderOpen(true);
              }}
            />
          )}
          <RentalContextIndicator />
        </div>

        <Space size="small" className="app-header__end">
          <RentalSwitcher />
          <Tooltip
            title={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            color={token.colorBgElevated}
            styles={{ body: { color: token.colorText } }}
          >
            <Button
              type="text"
              className="theme-toggle"
              role="switch"
              tabIndex={0}
              aria-checked={resolvedMode === 'dark'}
              aria-label={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              icon={resolvedMode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleMode}
            />
          </Tooltip>

          {user && (
            <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={['click']} arrow>
              <Space className="app-header__user" size="small">
                <Avatar
                  size={32}
                  icon={<UserOutlined />}
                  src={user.avatar}
                  style={{ backgroundColor: token.colorPrimary }}
                />
                {!isTouchTier && <Text style={{ color: token.colorText }}>{user.name}</Text>}
              </Space>
            </Dropdown>
          )}
        </Space>
      </AntHeader>
      <ImpersonationBanner />
    </>
  );
};
